/**
 * Self Test Milestone Generator
 * 
 * Genereert een "Persoonlijk Profiel" mijlpaal op basis van Self Test resultaten
 */

import { SelfTestResults } from './testDefinitions';
import { generateLearningAction } from '../utils/learningStyleActions';

export interface SelfTestMilestone {
  title: string;
  actions: Array<{
    title: string;
    enabled: boolean;
    source: string;
  }>;
  category: 'richting_keuzes';
  origin: 'selftest';
}

/**
 * Genereer mijlpaal met acties op basis van Self Test resultaten
 */
export function generateSelfTestMilestone(results: SelfTestResults): SelfTestMilestone {
  const actions: Array<{ title: string; enabled: boolean; source: string }> = [];
  
  // Acties op basis van rolvoorkeur (aangepast aan leerstijl)
  if (results.rolePreference) {
    const roleActions = getRoleBasedActions(results.rolePreference, results.learningStyle);
    actions.push(...roleActions);
  }
  
  // Acties op basis van kennisniveaus
  const knowledgeActions = getKnowledgeBasedActions(results.knowledgeLevels);
  actions.push(...knowledgeActions);
  
  // Algemene zelfontwikkeling acties
  actions.push(
    {
      title: 'Doe wekelijkse reflectie: wat ging goed, wat kan beter?',
      enabled: true,
      source: 'selftest'
    },
    {
      title: 'Identificeer je top 3 sterke punten en ontwikkelpunten',
      enabled: true,
      source: 'selftest'
    }
  );
  
  return {
    title: 'Persoonlijk Profiel & Zelfontwikkeling',
    actions,
    category: 'richting_keuzes',
    origin: 'selftest'
  };
}

/**
 * Rol-gebaseerde acties (aangepast aan leerstijl)
 */
function getRoleBasedActions(
  role: string, 
  learningStyle?: string
): Array<{ title: string; enabled: boolean; source: string }> {
  const roleTopicsMap: Record<string, string[]> = {
    doer: [
      'effectieve uitvoering en productiviteit',
      'prioriteren en delegeren van taken'
    ],
    thinker: [
      'strategisch denken en planning',
      'besluitvormingsframeworks'
    ],
    connector: [
      'netwerk- en samenwerkingsvaardigheden',
      'effectief communiceren met verschillende types'
    ],
    leader: [
      'leiderschapsstijlen en teamdynamiek',
      'visie creëren en anderen inspireren'
    ]
  };
  
  const topics = roleTopicsMap[role] || [];
  
  return topics.map(topic => ({
    title: generateLearningAction(topic, learningStyle as any),
    enabled: true,
    source: 'selftest'
  }));
}

/**
 * Kennis-gebaseerde acties
 */
function getKnowledgeBasedActions(
  knowledgeLevels: Record<string, string>
): Array<{ title: string; enabled: boolean; source: string }> {
  const actions: Array<{ title: string; enabled: boolean; source: string }> = [];
  
  // Tel hoevaak elk level voorkomt
  const levelCounts = {
    new: 0,
    learning: 0,
    familiar: 0,
    experienced: 0
  };
  
  Object.values(knowledgeLevels).forEach(level => {
    if (level in levelCounts) {
      levelCounts[level as keyof typeof levelCounts]++;
    }
  });
  
  // Bepaal dominante kennisniveau
  const totalDomains = Object.keys(knowledgeLevels).length;
  const newOrLearningCount = levelCounts.new + levelCounts.learning;
  
  if (newOrLearningCount > totalDomains / 2) {
    // Meeste domeinen zijn nieuw/learning
    actions.push({
      title: 'Maak een leerplan voor de komende 3 maanden',
      enabled: true,
      source: 'selftest'
    });
  } else if (levelCounts.experienced > totalDomains / 2) {
    // Meeste domeinen zijn experienced
    actions.push({
      title: 'Identificeer mogelijkheden om kennis te delen (mentor, training)',
      enabled: true,
      source: 'selftest'
    });
  } else {
    // Mix van levels
    actions.push({
      title: 'Focus op doorontwikkeling in je sterke domeinen',
      enabled: true,
      source: 'selftest'
    });
  }
  
  return actions;
}
